"use client";

import { sanitizeEmail, isValidEmail as validateEmail } from "@health/validation";
import { useCallback, useState } from "react";
import { useCompanionHost, useResolvedHostUrl } from "../../../host";

export type CaptchaState = {
  nonce: string;
  image?: string;
  audio?: string;
};

export function useAuthSignin(defaultEmail?: string) {
  const { onAuthChange, siteId } = useCompanionHost();
  const signInUrl = useResolvedHostUrl("/v1/auth/signin");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [email, setEmail] = useState(defaultEmail || "");
  const [password, setPassword] = useState("");

  const [captcha, setCaptcha] = useState<CaptchaState | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const resetCaptcha = useCallback(() => {
    setCaptcha(null);
    setCaptchaAnswer("");
  }, []);

  const submit = async (): Promise<{ success: boolean; user?: unknown }> => {
    setError("");

    const sanitizedEmail = sanitizeEmail(email);

    if (!validateEmail(sanitizedEmail)) {
      setError("Please enter a valid email address");
      return { success: false };
    }

    if (!password) {
      setError("Please enter your password");
      return { success: false };
    }

    if (captcha && !captchaAnswer.trim()) {
      setError("Please enter the captcha");
      return { success: false };
    }

    setIsLoading(true);
    try {
      const anonymousId =
        typeof window !== "undefined" ? localStorage.getItem("chatAnonymousId") : null;

      const response = await fetch(signInUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: sanitizedEmail,
          password,
          ...(siteId && { siteId }),
          ...(anonymousId && { anonymousId }),
          ...(captcha && { nonce: captcha.nonce, captcha: captchaAnswer.trim() }),
        }),
        credentials: "include",
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.message || "Sign in failed. Please check your credentials.");
        resetCaptcha();
        setIsLoading(false);
        return { success: false };
      }

      if (data.captchaRequired) {
        setCaptcha({
          nonce: data.nonce,
          image: data.captcha?.image,
          audio: data.captcha?.audio,
        });
        setCaptchaAnswer("");
        setIsLoading(false);
        return { success: false };
      }

      onAuthChange?.();
      resetCaptcha();
      setIsLoading(false);
      return { success: true, user: data.user };
    } catch (err) {
      console.error("Sign-in error:", err);
      setError("Unable to connect. Please check your internet connection and try again.");
      resetCaptcha();
      setIsLoading(false);
      return { success: false };
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    captcha,
    captchaAnswer,
    setCaptchaAnswer,
    resetCaptcha,
    isLoading,
    error,
    setError,
    submit,
  };
}
